// The playbook as an owner reads it (step 0.5): short plain-English sections built from a settings version.
//
// Two layers. The settings (ideal_customer, offer, voice, pipeline, human_gates ...) are what the engine runs on.
// The narrative under `playbook` is what the strategy agent writes for the owner: a one-paragraph summary, why
// customers choose them, a sample email, measures, open questions and where each statement came from. When the
// narrative is missing (older drafts, the demo), each section is written from the settings instead, and marked as
// such, so the owner can still judge it. Every section says what it sets up in the engine ("drives") and which
// settings keys it covers, so the detailed settings stay one click away.
import type { Json, JsonObject } from "./types";
import { humanise } from "./format";
import { GATE_LABELS } from "./config";

export type SectionId =
  | "business"
  | "customers"
  | "why_us"
  | "not_for"
  | "sources"
  | "firms"
  | "voice"
  | "journey"
  | "automation"
  | "measures"
  | "questions";

export type Confidence = "sure" | "likely" | "guess";

export interface Statement {
  text: string;
  source?: string;
  confidence?: Confidence;
}

export interface SourceRef {
  label: string;
  note?: string;
  confidence: Confidence;
}

interface Base {
  id: SectionId;
  /** "01" ... "11", as shown on the slide. */
  number: string;
  /** Short name, for the section list. */
  title: string;
  /** The slide's claim, a full sentence the owner agrees or disagrees with. */
  headline: string;
  lead?: string;
  statements: Statement[];
  sources: SourceRef[];
  /** What this section sets up in the engine, in plain words. */
  drives: string[];
  /** The settings keys behind it (for "changed in this draft" and the detailed settings). */
  configKeys: string[];
}

export interface Segment {
  name: string;
  detail?: string;
  priority?: string;
}
export interface Pool {
  id: "network" | "outbound" | "inbound" | "invited";
  name: string;
  blurb: string;
  on: boolean;
  feeds: string[];
}
export interface JourneyStage {
  key: string;
  label: string;
  goal: boolean;
  after: boolean;
}
export interface Gate {
  step: string;
  label: string;
  mode: "approve" | "auto";
  /** Always comes to a person: the playbook itself, and (once on) replies, meeting notes and offers. */
  locked: boolean;
}
/** The only gates an owner can ask to run on their own (docs/SECURITY.md 4a). */
export const AUTO_GATES = ["1.21", "4.10"];
export interface Measure {
  label: string;
  target?: string;
  note?: string;
}
export interface SampleEmail {
  to?: string;
  subject?: string;
  body: string;
}

export type PlaybookSection =
  | (Base & { kind: "text" })
  | (Base & { kind: "segments"; segments: Segment[]; facts: { label: string; value: string }[] })
  | (Base & { kind: "pools"; pools: Pool[] })
  | (Base & { kind: "firms" })
  | (Base & { kind: "voice"; rules: string[]; use: string[]; avoid: string[]; email: SampleEmail | null })
  | (Base & { kind: "journey"; stages: JourneyStage[]; deals: string[] })
  | (Base & { kind: "automation"; gates: Gate[] })
  | (Base & { kind: "measures"; measures: Measure[] });

export interface Playbook {
  name: string;
  /** True when the strategy agent wrote the narrative; false when every section was written from the settings. */
  narrative: boolean;
  sections: PlaybookSection[];
}

// ---------------------------------------------------------------------------- reading loose settings
const isObj = (v: unknown): v is JsonObject => typeof v === "object" && v !== null && !Array.isArray(v);
const obj = (v: Json | undefined): JsonObject => (isObj(v) ? v : {});
const str = (v: unknown): string => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");
/** A list of strings from a list of strings or of objects with a text-like field. */
function strings(v: Json | undefined): string[] {
  if (typeof v === "string") return v.trim() ? [v.trim()] : [];
  if (!Array.isArray(v)) return [];
  return v
    .map((x) => (isObj(x) ? str(x.text ?? x.label ?? x.name ?? x.key) : str(x)))
    .filter(Boolean);
}
const CONF = new Set<Confidence>(["sure", "likely", "guess"]);
function statementList(v: Json | undefined, fallbackSource?: string): Statement[] {
  if (!Array.isArray(v)) return strings(v).map((text) => ({ text, ...(fallbackSource ? { source: fallbackSource } : {}) }));
  return v
    .map((x): Statement | null => {
      if (isObj(x)) {
        const text = str(x.text);
        if (!text) return null;
        const c = str(x.confidence) as Confidence;
        return { text, ...(str(x.source) ? { source: str(x.source) } : fallbackSource ? { source: fallbackSource } : {}), ...(CONF.has(c) ? { confidence: c } : {}) };
      }
      const text = str(x);
      return text ? { text, ...(fallbackSource ? { source: fallbackSource } : {}) } : null;
    })
    .filter((s): s is Statement => s !== null);
}
const sentence = (s: string) => (s && !/[.!?]$/.test(s) ? `${s}.` : s);
const lower1 = (s: string) => (s ? s[0]!.toLowerCase() + s.slice(1) : s);
function joinList(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

function money(n: number, currency: string): string {
  const sym = currency === "GBP" ? "£" : currency === "EUR" ? "€" : currency === "USD" ? "$" : "";
  const v = n >= 1_000_000 ? `${+(n / 1_000_000).toFixed(1)}m` : n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
  return sym ? `${sym}${v}` : `${v} ${currency}`;
}

/** A size rule ({measure, min, max} or text) in words. */
export function sizeText(size: Json | undefined, currency = "GBP"): string {
  if (typeof size === "string") return size.trim();
  const s = obj(size);
  const measure = str(s.measure);
  const min = typeof s.min === "number" ? s.min : null;
  const max = typeof s.max === "number" ? s.max : null;
  if (min === null && max === null) return "";
  const isMoney = /gbp|eur|usd|revenue|profit|ebitda|turnover/i.test(measure);
  const cur = /eur/i.test(measure) ? "EUR" : /usd/i.test(measure) ? "USD" : currency;
  const f = (n: number) => (isMoney ? money(n, cur) : n.toLocaleString("en-GB"));
  const what = measure ? humanise(measure.replace(/_?(gbp|eur|usd)$/i, "")).replace(/^Ebitda/, "EBITDA").replace(/ proxy$/i, " (estimated)") : "Size";
  const range = min !== null && max !== null ? `${f(min)} to ${f(max)}` : min !== null ? `at least ${f(min)}` : `up to ${f(max!)}`;
  return `${what}: ${range}`;
}

const PRIORITY: Record<number, string> = { 1: "Main focus", 2: "Next", 3: "Only if the others run dry" };

function segmentsOf(ic: JsonObject): Segment[] {
  const segs = ic.segments;
  if (Array.isArray(segs) && segs.length) {
    return segs
      .map((x): Segment | null => {
        if (isObj(x)) {
          const name = str(x.label) || humanise(str(x.segment) || str(x.name));
          if (!name) return null;
          const lane = str(x.lane);
          const p = typeof x.priority === "number" ? x.priority : null;
          return { name, ...(str(x.description) ? { detail: str(x.description) } : lane ? { detail: humanise(lane) } : {}), ...(p ? { priority: PRIORITY[p] ?? `Priority ${p}` } : {}) };
        }
        const name = str(x);
        return name ? { name } : null;
      })
      .filter((s): s is Segment => s !== null);
  }
  return strings(ic.sectors).map((name) => ({ name }));
}

function geography(config: JsonObject): string[] {
  const ic = obj(config.ideal_customer);
  const geo = strings(ic.geography);
  if (geo.length) return geo;
  if (Array.isArray(config.markets)) {
    return config.markets
      .map((m) => (isObj(m) ? [str(m.country), ...strings(m.regions)].filter(Boolean).join(", ") : str(m)))
      .filter(Boolean);
  }
  return [];
}

function pools(config: JsonObject): Pool[] {
  const ls = obj(config.lead_sources);
  const standard = new Set(strings(ls.standard));
  const mailbox = obj(ls.mailbox);
  const enabled = (v: Json | undefined) => isObj(v) && v.enabled === true;
  const bs = obj(ls.business_specific);
  const inbound = obj(ls.inbound);
  const target = obj(config.target);
  const searchTerms = Object.values(obj(target.search_terms)).some((v) => strings(v).length > 0);
  const channels = obj(config.channels);

  const networkFeeds = [
    ...(standard.has("2.7") ? ["Asking past clients and referrers for introductions"] : []),
    ...(standard.has("2.8") || enabled(mailbox.introductions) ? ["Introductions that arrive by email"] : []),
  ];
  const outboundFeeds = [
    ...(Array.isArray(ls.registers) && ls.registers.length ? [`${ls.registers.length} industry ${ls.registers.length === 1 ? "register" : "registers"}`] : []),
    ...(isObj(ls.companies_house) || standard.has("2.2") ? ["Companies House filings"] : []),
    ...(searchTerms ? ["Map and directory listings"] : []),
    ...(isObj(ls.news_watch) || standard.has("2.4") ? ["News and job posts"] : []),
    ...(enabled(mailbox.deal_alerts) || enabled(mailbox.teasers) ? ["Deal alerts and broker teasers"] : []),
  ];
  if (!outboundFeeds.length && Object.keys(channels).length && isObj(channels.email) && channels.email.enabled === true) {
    outboundFeeds.push("New firms that match who buys from you");
  }
  const inboundFeeds = Object.entries(inbound)
    .filter(([, v]) => v === true || (Array.isArray(v) && v.length > 0))
    .map(([k, v]) => (Array.isArray(v) ? `${humanise(k)} (${strings(v).map(humanise).join(", ")})` : humanise(k)));
  const invitedFeeds = [...(enabled(bs.tenders) ? ["Tender portals"] : []), ...(enabled(bs.rfp_inbox) ? ["Requests for proposals by email"] : [])];

  return [
    { id: "network", name: "People who already know you", blurb: "Past clients, referrers and introductions.", on: networkFeeds.length > 0, feeds: networkFeeds },
    { id: "outbound", name: "Going out to find new customers", blurb: "Firms that match who buys from you, found and contacted by Ogma.", on: outboundFeeds.length > 0, feeds: outboundFeeds },
    { id: "inbound", name: "People who come to you", blurb: "Your website, newsletter and social channels.", on: inboundFeeds.length > 0, feeds: inboundFeeds },
    { id: "invited", name: "Tenders and invitations", blurb: "Work you are invited to bid for.", on: invitedFeeds.length > 0, feeds: invitedFeeds },
  ];
}

function stages(config: JsonObject): { stages: JourneyStage[]; deals: string[] } {
  const p = obj(config.pipeline);
  const label = (x: Json): { key: string; label: string; terminal: boolean } | null => {
    if (isObj(x)) {
      const key = str(x.key);
      return key ? { key, label: str(x.label) || humanise(key), terminal: x.terminal === true } : null;
    }
    const key = str(x);
    return key ? { key, label: humanise(key), terminal: false } : null;
  };
  const lead = (Array.isArray(p.lead_stages) ? p.lead_stages : []).map(label).filter((s): s is NonNullable<typeof s> => s !== null && !s.terminal);
  const goalIdx = lead.findIndex((s) => /meeting|booked|call_booked|site_visit|demo/.test(s.key));
  const out = lead.map((s, i) => ({ key: s.key, label: s.label, goal: i === goalIdx, after: goalIdx >= 0 && i > goalIdx }));
  const deals = (Array.isArray(p.deal_stages) ? p.deal_stages : [])
    .map(label)
    .filter((s): s is NonNullable<typeof s> => s !== null && !s.terminal)
    .map((s) => s.label);
  return { stages: out, deals };
}

function gates(config: JsonObject): Gate[] {
  const on = new Set(strings(config.human_gates));
  return Object.entries(GATE_LABELS).map(([step, label]) => ({
    step,
    label,
    mode: step === "0.5" || on.has(step) ? "approve" : "auto",
    locked: step === "0.5" || (on.has(step) && !AUTO_GATES.includes(step)),
  }));
}

/** Questions the settings leave open, in the owner's terms. Never more than six. */
function derivedQuestions(config: JsonObject): string[] {
  const out: string[] = [];
  const compliance = obj(config.compliance);
  if (compliance.gdpr_notice_confirmed === false) out.push("Have you confirmed how people are told that we hold their details (the privacy notice)?");
  const offer = obj(config.offer);
  if (!str(offer.lead_magnet)) out.push("Is there something useful we could give people for free, to start a conversation?");
  const voice = obj(config.voice);
  if (!(Array.isArray(voice.objections) && voice.objections.length) && !str(voice.objection_library)) out.push("What do people most often say when they turn you down, and how do you answer?");
  const tools = obj(config.tools);
  const newsletter = obj(config.newsletter);
  if (tools.newsletter && tools.newsletter !== "none" && "from_address" in newsletter && !str(newsletter.from_address)) out.push("Which email address should your newsletter come from?");
  const brand = obj(config.brand);
  if (!isObj(brand.colours) || !Object.keys(brand.colours).length) out.push("Do you have brand colours or a logo we should use?");
  return out;
}

const CONFIG_SOURCE: SourceRef = { label: "Your settings", note: "Written from the settings the strategy agent drafted", confidence: "likely" };

function sourcesFor(narrative: JsonObject, id: SectionId, fallback: SourceRef[]): SourceRef[] {
  const raw = obj(narrative.sources)[id];
  if (!Array.isArray(raw)) return fallback;
  const out = raw
    .map((x): SourceRef | null => {
      if (!isObj(x) || !str(x.label)) return null;
      const c = str(x.confidence) as Confidence;
      return { label: str(x.label), ...(str(x.note) ? { note: str(x.note) } : {}), confidence: CONF.has(c) ? c : "likely" };
    })
    .filter((s): s is SourceRef => s !== null);
  return out.length ? out : fallback;
}

// ---------------------------------------------------------------------------- the playbook
type Unnumbered = PlaybookSection extends infer T ? (T extends PlaybookSection ? Omit<T, "number"> : never) : never;

export function buildPlaybook(config: JsonObject, businessName?: string): Playbook {
  const narrative = obj(config.playbook);
  const hasNarrative = Object.keys(narrative).length > 0;
  const tenant = obj(config.tenant);
  const name = businessName?.trim() || str(tenant.name) || "Your business";
  const ic = obj(config.ideal_customer);
  const offer = obj(config.offer);
  const voice = obj(config.voice);
  const brand = obj(config.brand);
  const currency = str(config.currency) || "GBP";
  const headlines = obj(narrative.headlines);
  const head = (id: SectionId, fallback: string) => str(headlines[id]) || fallback;
  const src = (id: SectionId, fallback: SourceRef[] = [CONFIG_SOURCE]) => sourcesFor(narrative, id, fallback);

  const sections: Unnumbered[] = [];

  // 01 The business in one paragraph
  {
    const summary = str(narrative.summary);
    const description = str(ic.description);
    const core = str(offer.core_offer) || str(offer.headline);
    const built = [description && `${name} sells to ${lower1(description.replace(/\.$/, ""))}.`, core && sentence(core)].filter(Boolean).join(" ");
    sections.push({
      id: "business",
      kind: "text",
      title: "Your business",
      headline: head("business", `${name} in one paragraph`),
      lead: summary || built || "We don't have enough yet to describe your business. Tell us in your own words below.",
      statements: [],
      sources: src("business", summary ? [CONFIG_SOURCE] : [CONFIG_SOURCE]),
      drives: ["How Ogma describes you when it writes emails, posts and replies"],
      configKeys: ["tenant", "ideal_customer", "offer"],
    });
  }

  // 02 Who buys from you
  {
    const segments = segmentsOf(ic);
    const facts = [
      ...(sizeText(ic.size, currency) || str(ic.revenue) ? [{ label: "Size", value: sizeText(ic.size, currency) || str(ic.revenue) }] : []),
      ...(geography(config).length ? [{ label: "Where", value: joinList(geography(config)) }] : []),
      ...(strings(ic.must_have).length ? [{ label: "Must have", value: joinList(strings(ic.must_have)) }] : []),
      ...(str(ic.owner) ? [{ label: "Who decides", value: str(ic.owner) }] : []),
    ];
    sections.push({
      id: "customers",
      kind: "segments",
      title: "Who buys from you",
      headline: head("customers", segments.length ? `${segments.length === 1 ? "One kind" : `${segments.length} kinds`} of customer to go after` : "Who buys from you"),
      lead: str(ic.description) || undefined,
      statements: [],
      segments,
      facts,
      sources: src("customers"),
      drives: ["Which firms Ogma looks for, and in what order", "How each new firm is scored before anyone contacts it"],
      configKeys: ["ideal_customer", "markets", "target"],
    });
  }

  // 03 Why they pick you
  {
    const given = statementList(narrative.why_us);
    const fromSettings: Statement[] = [
      ...(str(offer.core_offer) || str(offer.headline) ? [{ text: sentence(str(offer.core_offer) || str(offer.headline)), source: "Your offer" }] : []),
      ...(str(brand.positioning) ? [{ text: sentence(str(brand.positioning)), source: "Your brand" }] : []),
      ...(str(brand.tagline) ? [{ text: `“${str(brand.tagline)}”`, source: "Your tagline" }] : []),
      ...(str(offer.lead_magnet) ? [{ text: `Something useful to start with: ${lower1(sentence(str(offer.lead_magnet)))}`, source: "Your offer" }] : []),
      ...(str(offer.call_to_action) || str(offer.ask) ? [{ text: `What we ask people to do: ${lower1(sentence(str(offer.call_to_action) || str(offer.ask)))}`, source: "Your offer" }] : []),
    ];
    sections.push({
      id: "why_us",
      kind: "text",
      title: "Why they pick you",
      headline: head("why_us", "Why customers choose you"),
      statements: given.length ? given : fromSettings,
      sources: src("why_us"),
      drives: ["The reasons Ogma gives in emails and posts", "What Ogma offers people to start a conversation"],
      configKeys: ["offer", "brand"],
    });
  }

  // 04 Who you don't want
  {
    const given = statementList(narrative.not_for);
    const excludes = strings(ic.exclude).map((t) => ({ text: sentence(t[0]!.toUpperCase() + t.slice(1)), source: "Your settings" }));
    sections.push({
      id: "not_for",
      kind: "text",
      title: "Who you don't want",
      headline: head("not_for", "Work Ogma will steer away from"),
      statements: given.length ? given : excludes,
      lead: !given.length && !excludes.length ? "Nothing is ruled out yet. Is there work you never want?" : undefined,
      sources: src("not_for"),
      drives: ["Firms Ogma will never contact", "What lowers a firm's score"],
      configKeys: ["ideal_customer"],
    });
  }

  // 05 Where work comes from
  {
    const ps = pools(config);
    const on = ps.filter((p) => p.on);
    sections.push({
      id: "sources",
      kind: "pools",
      title: "Where work comes from",
      headline: head("sources", on.length ? `${countWord(on.length)} ${on.length === 1 ? "way" : "ways"} new work will reach you` : "Where new work will come from"),
      statements: [],
      pools: ps,
      sources: src("sources"),
      drives: ["Which lead sources are switched on", "Which accounts you are asked to connect"],
      configKeys: ["lead_sources", "target", "tools"],
    });
  }

  // 06 Example firms (filled in on screen with real firms from the CRM)
  sections.push({
    id: "firms",
    kind: "firms",
    title: "Firms we'd approach",
    headline: head("firms", "Would you want work from these?"),
    lead: "Real firms Ogma has found that fit this playbook. Say yes or no to each; your answers teach it who to go after.",
    statements: [],
    sources: [{ label: "Firms found so far", note: "The best-scoring firms not yet contacted", confidence: "sure" }],
    drives: ["How new firms are ranked: each yes or no is passed to the strategy agent"],
    configKeys: [],
  });

  // 07 How you sound
  {
    const e = obj(narrative.sample_email);
    const email: SampleEmail | null = str(e.body) ? { body: str(e.body), ...(str(e.to) ? { to: str(e.to) } : {}), ...(str(e.subject) ? { subject: str(e.subject) } : {}) } : null;
    const person = str(voice.person).toLowerCase();
    const rules = [
      ...(str(voice.tone) ? [sentence(str(voice.tone))] : []),
      ...(person === "i" ? ["Written as you, in the first person (I)."] : person === "we" ? ["Written as the business (we)."] : []),
      ...strings(voice.rules).map(sentence),
    ];
    sections.push({
      id: "voice",
      kind: "voice",
      title: "How you sound",
      headline: head("voice", email ? "Would you send this?" : "How your messages will sound"),
      statements: [],
      rules,
      use: strings(brand.words_to_use),
      avoid: [...strings(brand.words_to_avoid), ...strings(voice.avoid)],
      email,
      sources: src("voice"),
      drives: ["The tone and length of every email Ogma drafts", "Words Ogma always or never uses"],
      configKeys: ["voice", "brand"],
    });
  }

  // 08 From first contact to meeting
  {
    const j = stages(config);
    const goal = j.stages.find((s) => s.goal);
    sections.push({
      id: "journey",
      kind: "journey",
      title: "First contact to meeting",
      headline: head("journey", goal ? `Ogma's job is to get you to “${goal.label.toLowerCase()}”` : "The steps from first contact to a sale"),
      statements: [],
      stages: j.stages,
      deals: j.deals,
      lead: j.stages.length ? undefined : "Your pipeline steps haven't been set yet. What do you call each step?",
      sources: src("journey"),
      drives: ["Your pipeline steps and their names", "Where Ogma's automatic work stops and yours starts"],
      configKeys: ["pipeline"],
    });
  }

  // 09 What runs on its own
  sections.push({
    id: "automation",
    kind: "automation",
    title: "What runs on its own",
    headline: head("automation", "What Ogma does without asking you"),
    statements: [],
    gates: gates(config),
    lead: "Anything marked “I approve” waits for you. Change any of these and the next draft will follow it.",
    sources: src("automation"),
    drives: ["Which steps wait for your approval"],
    configKeys: ["human_gates", "automation"],
  });

  // 10 What we'll measure
  {
    const given = Array.isArray(narrative.measures)
      ? narrative.measures
          .map((m): Measure | null => (isObj(m) && str(m.label) ? { label: str(m.label), ...(str(m.target) ? { target: str(m.target) } : {}), ...(str(m.note) ? { note: str(m.note) } : {}) } : null))
          .filter((m): m is Measure => m !== null)
      : [];
    const defaults: Measure[] = [
      { label: "Meetings booked each week", note: "The number that matters most" },
      { label: "New conversations each week", note: "People who replied" },
      { label: "Reply rate to first messages", note: "If it drops, the message changes" },
      { label: "New firms found each week", note: "Enough to keep the pipeline full" },
    ];
    sections.push({
      id: "measures",
      kind: "measures",
      title: "What we'll measure",
      headline: head("measures", "The numbers in your Monday summary"),
      statements: [],
      measures: given.length ? given : defaults,
      lead: given.some((m) => m.target) ? undefined : "Targets to agree with you: what would good look like?",
      sources: src("measures", given.length ? [CONFIG_SOURCE] : [{ label: "Ogma defaults", note: "No targets set yet", confidence: "guess" }]),
      drives: ["Your weekly summary and the targets in it"],
      configKeys: [],
    });
  }

  // 11 What we're not sure about
  {
    const qs = [...strings(narrative.open_questions), ...derivedQuestions(config)].slice(0, 6);
    sections.push({
      id: "questions",
      kind: "text",
      title: "What we're not sure about",
      headline: head("questions", qs.length ? `${countWord(qs.length)} ${qs.length === 1 ? "question" : "questions"} only you can answer` : "Nothing open"),
      statements: qs.map((text) => ({ text })),
      lead: qs.length ? undefined : "The draft didn't leave anything open. If something is missing, say so below.",
      sources: [{ label: "Gaps in what we know", note: "Things your answers and settings didn't settle", confidence: "guess" }],
      drives: ["Your answers update the sections they touch"],
      configKeys: ["compliance", "newsletter"],
    });
  }

  return {
    name,
    narrative: hasNarrative,
    sections: sections.map((s, i) => ({ ...s, number: String(i + 1).padStart(2, "0") }) as PlaybookSection),
  };
}

function countWord(n: number): string {
  return ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"][n] ?? String(n);
}

/** A section as plain lines (for the PowerPoint export and tests). Internal notes (sources, drives) are left out. */
export function sectionLines(s: PlaybookSection): string[] {
  const lines = [...(s.lead ? [s.lead] : []), ...s.statements.map((x) => x.text)];
  switch (s.kind) {
    case "segments":
      return [...lines, ...s.segments.map((g) => [g.name, g.priority, g.detail].filter(Boolean).join(" · ")), ...s.facts.map((f) => `${f.label}: ${f.value}`)];
    case "pools":
      return [...lines, ...s.pools.filter((p) => p.on).map((p) => `${p.name}${p.feeds.length ? `: ${p.feeds.join(", ")}` : ""}`)];
    case "voice":
      return [...lines, ...s.rules, ...(s.use.length ? [`Words we use: ${s.use.join(", ")}`] : []), ...(s.avoid.length ? [`Words we avoid: ${s.avoid.join(", ")}`] : [])];
    case "journey":
      return [...lines, s.stages.map((x) => x.label).join(" → "), ...(s.deals.length ? [`Then: ${s.deals.join(" → ")}`] : [])].filter(Boolean);
    case "automation":
      return [...lines, ...s.gates.map((g) => `${g.label}: ${g.mode === "approve" ? "you approve" : "runs on its own"}`)];
    case "measures":
      return [...lines, ...s.measures.map((m) => `${m.label}${m.target ? `: ${m.target}` : ""}`)];
    default:
      return lines;
  }
}

export const VERDICTS = [
  { key: "spot_on", label: "Spot on" },
  { key: "nearly", label: "Nearly" },
  { key: "wrong", label: "Wrong" },
] as const;
export type Verdict = (typeof VERDICTS)[number]["key"];
